import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth.jsx';
import { MONTHS, fmtPKR } from '../components/ui.jsx';

const STATUS_CHAR = { PRESENT: 'P', ABSENT: 'A', HALF_DAY: 'H', LEAVE: 'L' };

export default function Reports() {
  const { user } = useAuth();
  const now = new Date();
  const [tab, setTab] = useState('register');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [register, setRegister] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => { api('/sites').then(setSites).catch(() => {}); }, []);

  useEffect(() => {
    if (tab === 'register') {
      const params = new URLSearchParams({ year, month, ...(siteId ? { siteId } : {}) });
      api(`/reports/attendance-register?${params}`).then(setRegister).catch(() => setRegister(null));
    } else {
      api('/reports/workforce-summary').then(setSummary).catch(() => setSummary(null));
    }
  }, [tab, year, month, siteId]);

  const csvField = (v) => `"${String(v).replace(/"/g, '""')}"`;

  const exportCSV = () => {
    if (!register) return;
    const days = Array.from({ length: register.daysInMonth }, (_, i) => i + 1);
    const header = ['Code', 'Name', 'Designation', 'Site', 'Type', ...days, 'Present', 'Half', 'Absent', 'Leave', 'OT hrs'];
    const lines = register.rows.map((r) => [
      r.code, csvField(r.name), csvField(r.designation), csvField(r.site), r.employmentType,
      ...days.map((d) => r.days[d] ? STATUS_CHAR[r.days[d].status] : ''),
      r.totals.present, r.totals.half, r.totals.absent, r.totals.leave, r.totals.ot,
    ].join(','));
    const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `attendance-register-${year}-${String(month).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <div className="filter-bar no-print">
        <div className="segmented">
          <button className={tab === 'register' ? 'on' : ''} onClick={() => setTab('register')}>Attendance Register</button>
          <button className={tab === 'workforce' ? 'on' : ''} onClick={() => setTab('workforce')}>Workforce Summary</button>
        </div>
        <div style={{ flex: 1 }} />
        {tab === 'register' && (
          <>
            <select className="filter-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select className="filter-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[year - 2, year - 1, year].filter((v, i, a) => a.indexOf(v) === i)
                .map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            {!user.siteId && (
              <select className="filter-select" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">All Sites</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <button className="btn btn-ghost" onClick={exportCSV}>⬇ CSV</button>
          </>
        )}
        <button className="btn btn-primary" onClick={() => window.print()}>🖨 Print</button>
      </div>

      {tab === 'register' && register && (
        <div className="card emp-table-wrap print-area">
          <div className="payslip-header">
            <h2>Attendance Register — {MONTHS[month - 1]} {year}</h2>
            <p>{siteId ? sites.find((s) => s.id === siteId)?.name : 'All sites'} · {register.rows.length} employees · Generated {new Date().toLocaleDateString('en-GB')}</p>
          </div>
          <table className="register-table">
            <thead>
              <tr>
                <th>Code</th><th>Employee</th>
                {Array.from({ length: register.daysInMonth }, (_, i) => <th key={i}>{i + 1}</th>)}
                <th>P</th><th>H</th><th>A</th><th>OT</th>
              </tr>
            </thead>
            <tbody>
              {register.rows.map((r) => (
                <tr key={r.id}>
                  <td className="text-muted">{r.code}</td>
                  <td className="fw-bold" style={{ whiteSpace: 'nowrap' }}>{r.name}</td>
                  {Array.from({ length: register.daysInMonth }, (_, i) => {
                    const rec = r.days[i + 1];
                    return (
                      <td key={i}>
                        {rec
                          ? <span className={`register-cell ${STATUS_CHAR[rec.status]}`}
                              title={rec.ot ? `+${rec.ot}h OT` : rec.status}>{STATUS_CHAR[rec.status]}</span>
                          : <span className="register-cell dash">·</span>}
                      </td>
                    );
                  })}
                  <td className="text-green fw-bold">{r.totals.present}</td>
                  <td className="text-amber fw-bold">{r.totals.half}</td>
                  <td className="text-red fw-bold">{r.totals.absent}</td>
                  <td className="fw-bold">{r.totals.ot || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'workforce' && summary && (
        <div className="print-area">
          <div className="payslip-header">
            <h2>Workforce Summary</h2>
            <p>Generated {new Date().toLocaleDateString('en-GB')}</p>
          </div>
          <div className="dash-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="card">
              <div className="card-title">By Site</div>
              <table>
                <thead><tr><th>Site</th><th>Headcount</th><th>Avg Daily Rate</th></tr></thead>
                <tbody>
                  {summary.bySite.map((s) => (
                    <tr key={s.site}><td className="fw-bold">{s.site}</td><td>{s.count}</td><td>{fmtPKR(s.avgRate)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card">
              <div className="card-title">By Employment Type</div>
              <table>
                <thead><tr><th>Type</th><th>Headcount</th></tr></thead>
                <tbody>
                  {summary.byEmploymentType.map((t) => (
                    <tr key={t.type}>
                      <td className="fw-bold">{t.type}</td><td>{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="divider" />
              <div className="card-title">By Department</div>
              <table>
                <tbody>
                  {summary.byDepartment.map((d) => (
                    <tr key={d.department}><td className="fw-bold">{d.department}</td><td>{d.count}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
